import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import * as Sentry from '@sentry/react'
import { Button, Card, CardContent, CardHeader, Input, Label } from '../components/ui'
import { useAuth } from '../hooks/useAuth'
import { CheckCircle } from 'lucide-react'
import { api, logger } from '../utils/api'

interface InvitationDetails {
  id: string
  team_name: string
  inviter_name: string
  role: string
  contact: string
  contact_type: 'email' | 'phone'
  expires_at: number
}

export default function Login() {
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite')
  
  const [method, setMethod] = useState<'phone' | 'sso' | 'social'>('social')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null)
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  const { login } = useAuth()

  // Load invitation details if invite token is present
  useEffect(() => {
    if (inviteToken) {
      loadInvitationDetails()
    }
  }, [inviteToken])

  const loadInvitationDetails = async () => {
    if (!inviteToken) return

    try {
      logger.debug('Loading invitation details', { inviteToken })
      const data = await api.get(`/v1/invitations/${inviteToken}`, { skipAuth: true })
      setInvitation(data.invitation)
    } catch (err) {
      logger.error('Failed to load invitation', { error: err, inviteToken })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    return Sentry.startSpan(
      {
        op: 'auth.login',
        name: 'User Login',
        attributes: {
          method,
          hasInvite: !!inviteToken
        }
      },
      async () => {
        setLoading(true)
        
        try {
          logger.info('User attempting login', { method, hasInvite: !!inviteToken })
          
          if (method === 'phone') {
            await login({ phoneNumber, method: 'phone' })
            // Redirect to OTP verification page with invite token if present
            const otpUrl = `/auth/verify-otp?phone=${encodeURIComponent(phoneNumber)}`
            window.location.href = inviteToken ? `${otpUrl}&invite=${inviteToken}` : otpUrl
          } else if (method === 'sso') {
            await login({ email, method: 'sso', provider: 'enterprise', inviteToken: inviteToken || undefined })
          }
        } catch (error) {
          logger.error('Login failed', { error, method })
          Sentry.captureException(error, {
            tags: { 
              auth_method: method,
              has_invite: !!inviteToken 
            }
          })
          alert('Login failed. Please try again.')
        } finally {
          setLoading(false)
        }
      }
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-3xl font-bold text-gray-900">ClockinNout</h1>
          <p className="mt-2 text-center text-sm text-gray-600">
            {invitation ? `Sign in to join ${invitation.team_name}` : 'Sign in to your account'}
          </p>
        </div>

        {/* Invitation Details */}
        {invitation && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center mb-2">
              <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
              <span className="font-medium text-blue-900">Team Invitation</span>
            </div>
            <div className="text-sm text-blue-800">
              <p><strong>Company:</strong> {invitation.team_name}</p>
              <p><strong>Role:</strong> {invitation.role}</p>
              <p><strong>Invited by:</strong> {invitation.inviter_name}</p>
            </div>
          </div>
        )}

        <Card>
          <CardHeader>
            <div className="flex space-x-2 mb-4">
              <Button
                variant={method === 'social' ? 'primary' : 'ghost'}
                onClick={() => setMethod('social')}
                className="flex-1 text-sm"
              >
                Social Login
              </Button>
              <Button
                variant={method === 'phone' ? 'primary' : 'ghost'}
                onClick={() => setMethod('phone')}
                className="flex-1 text-sm"
              >
                Phone
              </Button>
              <Button
                variant={method === 'sso' ? 'primary' : 'ghost'}
                onClick={() => setMethod('sso')}
                className="flex-1 text-sm"
              >
                Enterprise SSO
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            {method === 'social' ? (
              <div className="space-y-3">
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full flex items-center justify-center gap-3"
                  onClick={async () => {
                    setLoadingProvider('google')
                    try {
                      logger.info('User attempting Google SSO login', { hasInvite: !!inviteToken })
                      await login({ method: 'sso', provider: 'google', inviteToken: inviteToken || undefined })
                    } catch (error) {
                      logger.error('Google login failed', { error })
                      Sentry.captureException(error, {
                        tags: { auth_provider: 'google' }
                      })
                      alert('Google login failed. Please try again.')
                    } finally {
                      setLoadingProvider(null)
                    }
                  }}
                  disabled={loadingProvider !== null}
                >
                  {loadingProvider === 'google' ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  className="w-full flex items-center justify-center gap-3"
                  onClick={async () => {
                    setLoadingProvider('microsoft')
                    try {
                      logger.info('User attempting Microsoft SSO login', { hasInvite: !!inviteToken })
                      await login({ method: 'sso', provider: 'microsoft', inviteToken: inviteToken || undefined })
                    } catch (error) {
                      logger.error('Microsoft login failed', { error })
                      Sentry.captureException(error, {
                        tags: { auth_provider: 'microsoft' }
                      })
                      alert('Microsoft login failed. Please try again.')
                    } finally {
                      setLoadingProvider(null)
                    }
                  }}
                  disabled={loadingProvider !== null}
                >
                  {loadingProvider === 'microsoft' ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 23 23">
                        <path fill="#f25022" d="M1 1h10v10H1z"/>
                        <path fill="#00a4ef" d="M12 1h10v10H12z"/>
                        <path fill="#7fba00" d="M1 12h10v10H1z"/>
                        <path fill="#ffb900" d="M12 12h10v10H12z"/>
                      </svg>
                      Continue with Microsoft
                    </>
                  )}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or use another method</span>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {method === 'phone' ? (
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="mt-1">
                    <PhoneInput
                      country={'us'}
                      value={phoneNumber}
                      onChange={(phone) => setPhoneNumber(phone)}
                      inputClass="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
                      containerClass="w-full"
                      buttonClass="border border-gray-300 rounded-l-md"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    We'll send you a verification code via SMS
                  </p>
                </div>
                ) : (
                  <div>
                    <Label htmlFor="email">Work Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your work email"
                      required
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      Sign in with your organization's SSO
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || (method === 'phone' && !phoneNumber) || (method === 'sso' && !email)}
                >
                  {loading ? 'Signing in...' : method === 'phone' ? 'Send Code' : 'Continue with SSO'}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center space-y-2">
              {inviteToken ? (
                <p className="text-sm text-gray-600">
                  New users will be automatically registered during sign in
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-blue-600 hover:text-blue-500 font-medium">
                    Sign up
                  </Link>
                </p>
              )}
              <p className="text-sm text-gray-600">
                Contact your administrator if you're having trouble signing in
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}